import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactElement, ReactNode, RefObject } from 'react'

export interface ScrollAreaProps {
    /** 外层容器类名：控制尺寸与布局（内层滚动视口会自动撑满并留出滚动条 gutter） */
    readonly className?: string
    /** 供外部拿到底层滚动视口（例如「滚到最新一条」） */
    readonly viewportRef?: RefObject<HTMLDivElement | null>
    /** 滚动视口类名（例如给内容加 `flex flex-col gap-2`） */
    readonly viewportClassName?: string
    readonly role?: string
    readonly ariaLive?: 'polite' | 'off'
    readonly children: ReactNode
}

/** 滑块最小高度：内容再长也要留出可抓取的长度 */
const MIN_THUMB_HEIGHT = 24
/** 右侧为滚动条预留的 gutter 宽度（px），滑块画在这条 gutter 里，因此永不压住内容 */
const THUMB_GUTTER = 'pr-3'

/** 滑块几何；`null` 表示内容未溢出，滑块整体隐藏 */
export interface ThumbGeometry {
    readonly top: number
    readonly height: number
}

/**
 * 由滚动状态算出滑块的位置与长度（纯函数，单独抽出来是为了能单测）。
 * 长度与「可视比例」成正比；位置与 scrollTop 线性对应，所以拖滑块与滚轮手感一致。
 */
export function computeThumbGeometry(
    scrollTop: number,
    scrollHeight: number,
    clientHeight: number,
): ThumbGeometry | null {
    // +1 是给子像素留的容差：取整后差 1px 时不该冒出一条满高的假滑块
    if (clientHeight <= 0 || scrollHeight <= clientHeight + 1) return null
    const proportional = (clientHeight / scrollHeight) * clientHeight
    // 上限用 clientHeight 封顶：视口本身比 24px 还矮时（极端窄条）滑块也不能溢出视口
    const height = Math.min(clientHeight, Math.max(MIN_THUMB_HEIGHT, proportional))
    const maxTop = clientHeight - height
    const scrollable = scrollHeight - clientHeight
    return { top: (scrollTop / scrollable) * maxTop, height }
}

/**
 * 自绘滚动条的滚动容器。
 *
 * 为什么不用原生滚动条：
 * - Windows 上是又粗又不透明的系统控件，和深色界面完全不搭；
 * - 移动端是**浮在内容之上**的 overlay，正好压在消息气泡右缘上。
 *
 * 所以这里彻底隐藏原生滚动条（见 app.css 的 `.native-scrollbar-hidden`），
 * 自己画一条半透明细滑块，并把它放进**为它预留的右侧 gutter** —— 内容与滑块互不重叠。
 * 滚动本身仍交给浏览器（滚轮 / 触摸 / 键盘 / 惯性全部原生），只接管视觉与拖拽。
 */
export function ScrollArea({
    className,
    viewportClassName,
    viewportRef,
    role,
    ariaLive,
    children,
}: ScrollAreaProps): ReactElement {
    const localRef = useRef<HTMLDivElement | null>(null)
    const viewport = viewportRef ?? localRef
    const [thumb, setThumb] = useState<ThumbGeometry | null>(null)
    const dragRef = useRef<{ startY: number; startScroll: number } | null>(null)

    const sync = useCallback(() => {
        const element = viewport.current
        if (!element) return
        setThumb(computeThumbGeometry(element.scrollTop, element.scrollHeight, element.clientHeight))
    }, [viewport])

    // 内容增删（新消息）与尺寸变化都要重算：每次渲染后同步一次最省心，代价只是几次只读属性访问
    useEffect(() => {
        sync()
    })

    useEffect(() => {
        const element = viewport.current
        if (!element) return
        const observer = new ResizeObserver(sync)
        observer.observe(element)
        return () => observer.disconnect()
    }, [sync, viewport])

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
        const element = viewport.current
        if (!element) return
        event.preventDefault()
        event.currentTarget.setPointerCapture(event.pointerId)
        dragRef.current = { startY: event.clientY, startScroll: element.scrollTop }
    }

    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
        const element = viewport.current
        const drag = dragRef.current
        if (!element || !drag || !thumb) return
        const maxTop = element.clientHeight - thumb.height
        if (maxTop <= 0) return
        const delta = ((event.clientY - drag.startY) / maxTop) * (element.scrollHeight - element.clientHeight)
        element.scrollTop = drag.startScroll + delta
    }

    const endDrag = (event: ReactPointerEvent<HTMLDivElement>): void => {
        dragRef.current = null
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId)
        }
    }

    return (
        <div className={`relative flex min-h-0 flex-col ${className ?? ''}`}>
            <div
                ref={viewport}
                role={role}
                aria-live={ariaLive}
                onScroll={sync}
                className={`native-scrollbar-hidden min-h-0 flex-1 overflow-y-auto overscroll-contain ${THUMB_GUTTER} ${viewportClassName ?? ''}`}
            >
                {children}
            </div>
            {thumb ? (
                <div
                    aria-hidden="true"
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    className="absolute right-1 w-1.5 rounded-full bg-white/25"
                    style={{ top: thumb.top, height: thumb.height }}
                />
            ) : null}
        </div>
    )
}
